"use client";

import Link from "next/link";
import { Home, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { AtriamLogo } from "@/components/branding/atriam-logo";

// Ambient gradient shape - abstract, professional light forms
function AmbientGradient({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`pointer-events-none absolute ${className ?? ""}`}
      style={style}
    />
  );
}

// Animated 404 digit with liquid glass effect
function GlassDigit({ digit, index }: { digit: string; index: number }) {
  return (
    <div
      className="animate-digit-float relative inline-flex items-center justify-center"
      style={{
        animationDelay: `${String(index * 0.15)}s`,
      }}
    >
      {/* Main digit container */}
      <div
        data-liquid-glass="elevated"
        className="relative rounded-3xl border px-3 py-4 sm:rounded-[2.5rem] sm:px-6 sm:py-8"
      >
        {/* The digit */}
        <span
          className="font-display relative select-none text-[4.5rem] font-bold leading-none tracking-tighter sm:text-[8rem] md:text-[10rem]"
          style={{
            background:
              "linear-gradient(180deg, var(--foreground) 0%, oklch(0.6 0 0) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: "0 4px 30px oklch(0 0 0 / 0.1)",
          }}
        >
          {digit}
        </span>
      </div>
    </div>
  );
}

export default function NotFound() {
  return (
    <div className="bg-background relative flex min-h-dvh flex-col items-center justify-center overflow-hidden">
      {/* Static ambient glow — single subtle radial from the same palette */}
      <div
        className="absolute inset-0 opacity-60 dark:opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% 30%, oklch(0.5 0.12 42 / 0.15), transparent 50%)",
        }}
      />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.02] dark:opacity-[0.04]"
        style={{
          backgroundImage: `
            linear-gradient(oklch(var(--liquid-glass-stroke-base)) 1px, transparent 1px),
            linear-gradient(90deg, oklch(var(--liquid-glass-stroke-base)) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Noise texture overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.015] mix-blend-overlay dark:opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Ambient gradient shapes - sophisticated light forms */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Primary orange glow - top right */}
        <AmbientGradient
          className="animate-drift-slow"
          style={{
            width: "60%",
            height: "50%",
            top: "-10%",
            right: "-15%",
            background:
              "radial-gradient(ellipse at center, oklch(0.45 0.12 42 / 0.15), transparent 70%)",
            filter: "blur(60px)",
          }}
        />

        {/* Secondary sky accent - bottom left */}
        <AmbientGradient
          className="animate-drift-medium"
          style={{
            width: "50%",
            height: "45%",
            bottom: "-5%",
            left: "-10%",
            background:
              "radial-gradient(ellipse at center, oklch(0.4 0.13 220 / 0.12), transparent 70%)",
            filter: "blur(80px)",
          }}
        />

        {/* Tertiary cyan highlight - center right */}
        <AmbientGradient
          className="animate-drift-fast"
          style={{
            width: "35%",
            height: "40%",
            top: "40%",
            right: "5%",
            background:
              "radial-gradient(ellipse at center, oklch(0.45 0.1 195 / 0.08), transparent 65%)",
            filter: "blur(50px)",
          }}
        />

        {/* Subtle orange underlayer - bottom */}
        <AmbientGradient
          style={{
            width: "80%",
            height: "30%",
            bottom: "0",
            left: "10%",
            background:
              "linear-gradient(180deg, transparent, oklch(0.4 0.08 42 / 0.06))",
            filter: "blur(40px)",
          }}
        />
      </div>

      {/* Main content */}
      <div className="stagger-reveal relative z-10 flex flex-col items-center px-6">
        {/* 404 digits */}
        <div className="mb-6 flex items-center gap-1 sm:mb-10 sm:gap-3">
          <GlassDigit digit="4" index={0} />
          <GlassDigit digit="0" index={1} />
          <GlassDigit digit="4" index={2} />
        </div>

        {/* Glass message card */}
        <div
          data-liquid-glass="elevated"
          className="relative w-full max-w-md rounded-2xl border p-6 text-center sm:rounded-3xl sm:p-8"
        >
          <div className="relative space-y-4">
            <h1 className="font-display text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
              Page not found
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
              The page you&apos;re looking for has drifted into the void.
              Let&apos;s get you back on track.
            </p>

            {/* Action buttons */}
            <div className="flex flex-col justify-center gap-3 pt-4 sm:flex-row">
              <Button
                asChild
                className="group relative overflow-hidden"
                size="lg"
              >
                <Link href="/">
                  <Home className="mr-2 h-4 w-4 transition-transform group-hover:scale-110" />
                  Go home
                </Link>
              </Button>

              <Button
                variant="outline"
                className="group"
                size="lg"
                onClick={() => window.history.back()}
              >
                <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
                Go back
              </Button>
            </div>
          </div>
        </div>

        {/* Subtle tagline */}
        <div className="mt-8 flex flex-col items-center gap-2">
          <AtriamLogo className="opacity-80" wordmarkClassName="text-sm" />
          <p className="text-muted-foreground/60 text-xs uppercase tracking-label">
            Build Better Teams
          </p>
        </div>
      </div>
    </div>
  );
}
